import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Stack,
  Chip,
  styled,
  Button,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Grid,
  Portal,
  ClickAwayListener,
  useMediaQuery,
} from "@mui/material";
import {
  Search,
  Filter,
  MoreVertical,
  ChevronRight,
  Ticket as TicketIcon,
  Clock,
  CheckCircle2,
  AlertCircle,
  Calendar as CalendarIcon,
  X as CloseIcon
} from "lucide-react";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "./TicketTable.css"; // We'll create this for calendar styling
import TicketCards from "./Cards";
import { useNavigate } from "react-router-dom";
import apiEndPointsConfig from "../../services/apiEndPoints";
import axiosInstance from "../../services/axiosInstance";
import SideNavbar from "../../components/sideNavbar";

const GlassCard = styled(Box)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(12px)',
  borderRadius: '24px',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02)',
  border: '1px solid #F0F0F5',
  padding: '24px',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.06)',
  },
}));

const StyledHeaderCell = styled(TableCell)(({ theme }) => ({
  color: '#6B7280',
  fontWeight: 700,
  fontSize: '12px',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  padding: '16px 24px',
  borderBottom: '1px solid #F3F4F6',
}));

const StyledBodyCell = styled(TableCell)(({ theme }) => ({
  padding: '20px 24px',
  fontSize: '14px',
  color: '#1F2937',
  borderBottom: '1px solid #F9FAFB',
}));

const StatusBadge = ({ status }) => {
  const getStatusStyles = (status) => {
    switch (status?.toLowerCase()) {
      case "open":
        return { bg: '#ECFDF5', color: '#059669', icon: <AlertCircle size={14} /> };
      case "in progress":
        return { bg: '#FFF7ED', color: '#D97706', icon: <Clock size={14} /> };
      case "closed":
        return { bg: '#FEF2F2', color: '#DC2626', icon: <CheckCircle2 size={14} /> };
      case "waiting on customer":
        return { bg: '#EFF6FF', color: '#2563EB', icon: <Clock size={14} /> };
      default:
        return { bg: '#F3F4FB', color: '#6366F1', icon: <TicketIcon size={14} /> };
    }
  };

  const styles = getStatusStyles(status);

  return (
    <Box sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 1,
      px: 1.5,
      py: 0.5,
      borderRadius: '20px',
      bgcolor: styles.bg,
      color: styles.color,
      fontWeight: 600,
      fontSize: '12px'
    }}>
      {styles.icon}
      {status}
    </Box>
  );
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  if (isNaN(date)) return "Invalid Date";
  return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
};

const TicketTable = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const userId = localStorage.getItem("userId");

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [cardDetails, setCardDetails] = useState([]);
  const [stats, setStats] = useState({});
  const [selectedDateRange, setSelectedDateRange] = useState([]);
  const [calendarVisible, setCalendarVisible] = useState(false);
  const isMobile = useMediaQuery("(max-width:600px)");

  useEffect(() => {
    getAllTicket();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [data, selectedDateRange]);

  const applyFilters = () => {
    let filtered = data;

    if (selectedDateRange?.length === 2) {
      const [startDate, endDate] = selectedDateRange;
      const start = new Date(startDate).setHours(0, 0, 0, 0);
      const end = new Date(endDate).setHours(23, 59, 59, 999);

      filtered = filtered.filter((ticket) => {
        const ticketDate = new Date(ticket.createdAt).getTime();
        return ticketDate >= start && ticketDate <= end;
      });
    }

    setFilteredData(filtered);
  };

  const clearDates = (e) => {
    e.stopPropagation();
    setSelectedDateRange([]);
    setCalendarVisible(false);
  };

  const getAllTicket = async () => {
    try {
      const res = await axiosInstance.get(
        `${apiEndPointsConfig.getCustomerTicketByUser}/${userId}`,
        {
          headers: {
            Authorization: `${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      setData(res?.data?.data || []);
      setStats(res?.data?.stats || {});
      setCardDetails(res?.data?.data || []);
    } catch (error) {
      console.error("Error fetching tickets:", error);
    }
  };

  return (
    <Box sx={{ bgcolor: '#F8F9FC', minHeight: '100vh', display: { xs: 'block', md: 'flex' } }}>
      <Box sx={{ flexGrow: 1, width: { xs: '100%', md: '100%' }, p: { xs: 2, md: 3 }, pt: { xs: 2, md: 3 } }}>
        <Box sx={{ mb: 4 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
          >
            <Box>
              <Typography variant="h4" fontWeight={800} color="#111827">Support Tickets</Typography>
              <Typography variant="body2" color="#6B7280">Track and manage your support requests easily.</Typography>
            </Box>
            <Stack direction="row" spacing={2} alignItems="center">
              {/* Selected Date Range Button */}
              {selectedDateRange?.length === 2 && (
                <Box
                  sx={{
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2,
                    py: 1,
                    backgroundColor: "#f1f5ff",
                    color: "#4F46E5",
                    fontWeight: 600,
                    fontSize: 13,
                    position: "relative",
                    gap: 1,
                    border: '1px solid #E0E7FF'
                  }}
                >
                  {`${selectedDateRange[0]?.toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                  })} - ${selectedDateRange[1]?.toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                  })}`}
                  <IconButton
                    size="small"
                    onClick={clearDates}
                    sx={{
                      p: 0.2,
                      backgroundColor: "white",
                      color: "#4F46E5",
                      "&:hover": {
                        backgroundColor: "#4F46E5",
                        color: "white",
                      },
                    }}
                  >
                    <CloseIcon size={14} />
                  </IconButton>
                </Box>
              )}

              {/* Calendar Component Logic */}
              {calendarVisible && (
                <Portal>
                  <ClickAwayListener
                    mouseEvent="onClick"
                    touchEvent="onTouchStart"
                    onClickAway={(e) => {
                      if (e.target.closest(".calendar-toggle")) return;
                      setCalendarVisible(false);
                    }}
                  >
                    <Box
                      sx={{
                        position: "fixed",
                        top: isMobile ? "53%" : "80px",
                        left: isMobile ? "50%" : "auto",
                        right: isMobile ? "auto" : "40px",
                        transform: isMobile ? "translate(-50%, -50%)" : "none",
                        zIndex: 1300,
                        boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                        borderRadius: 3,
                        overflow: "hidden",
                        backgroundColor: "#fff",
                        width: isMobile ? "90%" : "auto",
                        border: '1px solid #E5E7EB'
                      }}
                    >
                      <Calendar
                        selectRange
                        onChange={(range) => {
                          setSelectedDateRange(range);
                          setCalendarVisible(false);
                        }}
                        value={selectedDateRange}
                        maxDate={new Date()}
                        prev2Label={null}
                        next2Label={null}
                      />
                    </Box>
                  </ClickAwayListener>
                </Portal>
              )}

              {/* Calendar Toggle Button */}
              <Box
                className="calendar-toggle"
                sx={{
                  border: "1px solid #d0d0d0",
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 1.2,
                  px: 2,
                  backgroundColor: "#6366f1",
                  boxShadow: "0 4px 12px rgba(99, 102, 241, 0.2)",
                  gap: 1,
                  cursor: "pointer",
                  color: "white",
                  transition: "all 0.3s ease",
                  width: "fit-content",
                  '&:hover': {
                    backgroundColor: '#4F46E5',
                    transform: 'translateY(-1px)'
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setCalendarVisible((prev) => !prev);
                }}
              >
                <CalendarMonthIcon sx={{ fontSize: 20, color: "white" }} />
                <Typography fontSize={14} fontWeight={600}>
                  Date
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </Box>

        <TicketCards cardDetails={filteredData} stats={stats} />

        <GlassCard sx={{ mt: 4, p: 0, overflow: 'hidden' }}>
          <Box sx={{ p: 3, borderBottom: '1px solid #F3F4F6' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
              <Box sx={{ position: 'relative', width: '100%', flex: 1 }}>
                <Search size={18} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
                <input
                  type="text"
                  placeholder="Search tickets..."
                  style={{
                    width: '100%',
                    padding: '12px 16px 12px 48px',
                    borderRadius: '12px',
                    border: '1px solid #E5E7EB',
                    outline: 'none',
                    fontSize: '14px',
                    backgroundColor: '#F9FAFB'
                  }}
                />
              </Box>
              <IconButton sx={{
                bgcolor: '#F9FAFB',
                border: '1px solid #E5E7EB',
                borderRadius: '12px',
                width: { xs: '100%', sm: 'auto' },
                height: { xs: '48px', sm: 'auto' }
              }}>
                <Filter size={20} color="#6B7280" />
              </IconButton>
            </Stack>
          </Box>

          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledHeaderCell>Title</StyledHeaderCell>
                  <StyledHeaderCell>Issue</StyledHeaderCell>
                  <StyledHeaderCell>Contact</StyledHeaderCell>
                  <StyledHeaderCell>Status</StyledHeaderCell>
                  <StyledHeaderCell>Date Created</StyledHeaderCell>
                  <StyledHeaderCell align="right"></StyledHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 10 }}>
                      <Box sx={{ opacity: 0.5, mb: 2 }}>
                        <TicketIcon size={48} />
                      </Box>
                      <Typography variant="h6" color="textSecondary">No tickets found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredData.map((row, index) => (
                    <TableRow
                      key={row._id}
                      hover
                      sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#F9FAFB' } }}
                      onClick={() => navigate(`/user/tickets/${row._id}`)}
                    >
                      <StyledBodyCell sx={{ minWidth: '150px' }}>
                        <Typography fontWeight={700} color="#4F46E5" sx={{ fontSize: '13px' }}>
                          {row.title}
                        </Typography>
                      </StyledBodyCell>
                      <StyledBodyCell sx={{ minWidth: '250px' }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            maxWidth: '350px', 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap' 
                          }}
                        >
                          {row.issue}
                        </Typography>
                      </StyledBodyCell>
                      <StyledBodyCell sx={{ minWidth: '180px' }}>
                        <Typography fontWeight={600}>{row.name}</Typography>
                        <Typography variant="caption" color="textSecondary">{row.mobile}</Typography>
                      </StyledBodyCell>
                      <StyledBodyCell sx={{ minWidth: '140px' }}>
                        <StatusBadge status={row.status} />
                      </StyledBodyCell>
                      <StyledBodyCell sx={{ minWidth: '130px' }}>{formatDate(row.createdAt)}</StyledBodyCell>
                      <StyledBodyCell align="right" sx={{ width: '50px' }}>
                        <IconButton size="small">
                          <ChevronRight size={20} color="#9CA3AF" />
                        </IconButton>
                      </StyledBodyCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </GlassCard>
      </Box>
    </Box>
  );
};

export default TicketTable;
